import type { DialogManagerActive } from './atoms';

/**
 * Tracks which callback reported that one `handlePlanAppointments` call is over.
 *
 * The util reports through several callbacks and, on some paths, through none of
 * them: a `success: false` response that carries no planning entities opens no
 * dialog and reaches neither `onSuccess` nor `onCancelWarningDialog`. Callers
 * hold drop feedback (the frozen preview, the per-column loading state) until
 * one of them fires, so a path that reports nothing would strand it until a
 * watchdog fires — hence `onComplete` as a last resort, and hence needing to
 * know whether anything already accounted for the call.
 *
 * The warning dialog is the other half: while it waits on the user the call is
 * not finished, but nothing is in flight either, so the dialog owns it until the
 * user answers. Its retry re-enters the util with the *same* options object —
 * and therefore the same tracker — so `trackDialogState` hands ownership back on
 * submit, otherwise the last resort would skip a retry that reports through
 * nothing.
 *
 * Callers still decide *what* to release; this only decides *whether* to.
 */
export interface PlanAppointmentsTerminalTracker {
  /**
   * Wrap the caller's dialog setter. Marks the warning dialog as owning the
   * call, and returns ownership when the user submits the retry.
   */
  trackDialogState: (
    setDialogState: (dialogState: DialogManagerActive) => void
  ) => (dialogState: DialogManagerActive) => void;
  /**
   * Wrap a callback that accounts for the call — `onSuccess`,
   * `onCancelWarningDialog`, `onMutationError`.
   */
  report: (handler: (error?: unknown) => void) => (error?: unknown) => void;
  /**
   * Wrap `onComplete`: runs the handler only when nothing else accounted for the
   * call and no dialog is waiting on the user. Callers use it to clear the drop
   * feedback a path that reported nothing would otherwise strand.
   */
  runIfNothingElseReported: (handler: () => void) => () => void;
}

export const createPlanAppointmentsTerminalTracker =
  (): PlanAppointmentsTerminalTracker => {
    let accountedFor = false;
    let dialogOwnsCall = false;

    return {
      trackDialogState: (setDialogState) => (dialogState) => {
        dialogOwnsCall = true;
        setDialogState({
          ...dialogState,
          onSubmit: () => {
            // Re-armed by this same wrapper if the retry opens another dialog.
            dialogOwnsCall = false;
            dialogState.onSubmit?.();
          }
        });
      },
      report: (handler) => (error) => {
        accountedFor = true;
        handler(error);
      },
      runIfNothingElseReported: (handler) => () => {
        if (accountedFor || dialogOwnsCall) return;
        handler();
      }
    };
  };
